import {
  EnvironmentProviders,
  Provider,
  Type,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { Routes, provideRouter } from '@angular/router';
import { provideAppStarter } from './app-starter.providers';
import { provideAppStarterForcedConsent } from './app-starter-forced-consent.providers';
import { provideAppStarterNoAuth } from './app-starter-no-auth.providers';

type RouterFeature = Parameters<typeof provideRouter>[1];

/**
 * Authentication bootstrap profile an app runs under. Selects which app-starter
 * providers (and their auth wiring + init side-effects) `provideAppCore` pulls
 * in:
 * - `'msal'` — standard MSAL/B2C apps (meta-app, meta-at, meta-ch, admin,
 *   kost-import). Full init (page title, current user,
 *   Zendesk, version check) + global error listeners.
 * - `'forced-consent'` — apps behind the forced-consent flow (hartmann).
 * - `'none'` — headless/tooling apps with no user auth (headless-codes).
 */
export type AppAuthMode = 'msal' | 'forced-consent' | 'none';

export interface AppCoreOptions {
  /** Authentication bootstrap profile the app runs under. */
  auth: AppAuthMode;
  /** Routed security composition is owned by the application. */
  securityProviders: Array<Provider | EnvironmentProviders>;
  appRoutingModule?: Type<unknown>;
  routes?: Routes;
  routerFeatures?: RouterFeature[];
  withAnimations?: boolean;
}

function starterProviders(
  auth: AppAuthMode,
  securityProviders: Array<Provider | EnvironmentProviders>,
): Array<Provider | EnvironmentProviders> {
  switch (auth) {
    case 'msal':
      return provideAppStarter(securityProviders);
    case 'forced-consent':
      return provideAppStarterForcedConsent(securityProviders);
    case 'none':
      return provideAppStarterNoAuth(securityProviders);
  }
}

/**
 * Single entry point for application core providers, parameterised by auth mode.
 *
 * Replaces the previous split between `provideAppCoreProviders()` (which always
 * imported the MSAL starter) and apps that hand-rolled the same browser/router/
 * error scaffolding around the forced-consent / no-auth starters directly.
 * Applications compose optional capability providers alongside this bootstrap.
 *
 * - Always includes `BrowserModule` (before the starter providers, so the
 *   starter's `BdUserErrorHandler` wins over BrowserModule's default —
 *   BDA-5426) and the selected starter providers.
 * - `'msal'` / `'forced-consent'` additionally register
 *   `provideBrowserGlobalErrorListeners()` (route async errors to Dash0) and
 *   `BrowserAnimationsModule`; `'none'` does neither, matching the leaner
 *   headless bootstrap.
 * - Includes animations when `withAnimations` is true (default).
 * - Use either `appRoutingModule` OR `routes` (+ optional `routerFeatures`).
 */
export function provideAppCore(
  options: AppCoreOptions,
): Array<Provider | EnvironmentProviders> {
  const withAnimations = options.withAnimations !== false;
  const isNoAuth = options.auth === 'none';

  const providers: Array<Provider | EnvironmentProviders> = [
    // BrowserModule must come BEFORE the app-starter providers. BrowserModule
    // registers a default `{ provide: ErrorHandler, useFactory: () => new ErrorHandler() }`
    // and, for non-multi providers, the last one wins — so the starter's
    // `{ provide: ErrorHandler, useClass: BdUserErrorHandler }` is silently
    // overridden when listed first, leaving the default ErrorHandler in place.
    // That meant Angular caught application errors and printed them via
    // `console.error('ERROR', ...)` but never invoked BdUserErrorHandler →
    // trackException → Dash0 reportError (BDA-5426). Do not reorder these two.
    importProvidersFrom(
      BrowserModule,
      ...(withAnimations && !isNoAuth ? [BrowserAnimationsModule] : []),
    ),
    ...starterProviders(options.auth, options.securityProviders),
  ];

  // Route window `error` / `unhandledrejection` events into Angular's
  // ErrorHandler. Without this, zoneless apps (provideZonelessChangeDetection)
  // only forward errors that surface synchronously during change detection;
  // anything thrown from setTimeout, ResizeObserver, promise chains or other
  // async callbacks reaches the browser console but never BdUserErrorHandler
  // → trackException → Dash0 reportError (BDA-5426). The headless no-auth
  // profile historically runs without this and is preserved as-is.
  if (!isNoAuth) {
    providers.push(provideBrowserGlobalErrorListeners());
  }

  if (options.appRoutingModule) {
    providers.push(importProvidersFrom(options.appRoutingModule));
  } else if (options.routes) {
    providers.push(
      provideRouter(options.routes, ...(options.routerFeatures ?? [])),
    );
  }

  if (withAnimations) {
    providers.push(provideAnimations());
  }

  return providers;
}
